import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { PL_ROUTING } from '@common/constant';
import { delay, take, filter, timeout, catchError } from 'rxjs/operators';
import { firstValueFrom, combineLatest, of } from 'rxjs';
import { selectCurrentUserLoaded, selectCurrentToken } from 'pl-core-services';
import { AuthSyncService } from './auth-sync.service';
import { isValidReturnUrl } from './return-url.utils';

@Component({
  // NOTE: Empty template intentional - user will not see this component
  // as we redirect immediately after processing the OAuth callback
  template: '',
})
export class AuthCallbackComponent implements OnInit {
  private readonly RETURN_URL_KEY = 'auth_return_url';

  constructor(
    private router: Router,
    private oidcSecurityService: OidcSecurityService,
    private authSyncService: AuthSyncService,
    private store: Store
  ) {}

  /**
   * Retrieves and clears the stored return URL from sessionStorage.
   * Falls back to homepage if no URL was stored or URL is invalid.
   * Validates URL to prevent open redirects and loops.
   */
  private getAndClearReturnUrl(): string {
    const returnUrl = sessionStorage.getItem(this.RETURN_URL_KEY);
    const defaultUrl = `/${PL_ROUTING.LANDING_HOME}`;

    if (returnUrl) {
      sessionStorage.removeItem(this.RETURN_URL_KEY);

      // Validate the URL to prevent security issues
      // Must start with '/' (relative URL only, no external redirects)
      // Must not be the callback URL itself (prevent loops)
      if (isValidReturnUrl(returnUrl)) {
        return returnUrl;
      }
    }

    return defaultUrl;
  }

  /**
   * Waits for BOTH the user to be available in the Redux store AND the token.
   * Uses existing selectors from pl-core-services:
   * - selectCurrentUserLoaded: checks if user exists and has uuid
   * - selectCurrentToken: checks if token exists
   * Returns true if both are available, false if timeout.
   */
  private async waitForUserInStore(): Promise<boolean> {
    try {
      const bothReady = await firstValueFrom(
        combineLatest([
          this.store.select(selectCurrentUserLoaded),
          this.store
            .select(selectCurrentToken)
            .pipe(filter((token) => !!token)),
        ]).pipe(
          filter(([userLoaded]) => userLoaded),
          take(1),
          timeout(1000),
          catchError(() => {
            // eslint-disable-next-line no-console
            console.log('[AuthCallback] Timeout waiting for user and token');
            return of([false, null]);
          })
        )
      );

      const [userLoaded, token] = bothReady;
      if (userLoaded && token) {
        // eslint-disable-next-line no-console
        console.log(
          '[AuthCallback] User and token are now available in store - safe to navigate'
        );
        return true;
      }
      // eslint-disable-next-line no-console
      console.log('[AuthCallback] User or token not available after timeout', {
        hasUser: userLoaded,
        hasToken: !!token,
      });
      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '[AuthCallback] Error waiting for user and token in store:',
        error
      );
      return false;
    }
  }

  /**
   * Waits for user/token with proper timeout handling.
   * Logs warnings if timeout occurs but proceeds with navigation (graceful degradation).
   * Guards will handle missing auth data if wait fails.
   */
  private async waitForUserOrProceed(): Promise<void> {
    const ready = await this.waitForUserInStore();
    if (!ready) {
      // eslint-disable-next-line no-console
      console.warn(
        '[AuthCallback] Proceeding with navigation despite timeout - guards may redirect to login if auth incomplete'
      );
    }
  }

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('code') && urlParams.get('state')) {
      this.oidcSecurityService.isAuthenticated$
        .pipe(
          delay(500), // Give the OIDC client time to process before checking auth...
          take(1)
        )
        .subscribe({
          next: () => {
            // NOTE: In all cases, whether error or not,
            // I'm opting to attempt a safe redirect to home page.
            // If they are not authenticated, they will be redirected
            // to the respective configured /login route anyway.
            // If they are authenticated, great - they can continue
            // about their business.
            this.oidcSecurityService.checkAuth().subscribe({
              next: async (result) => {
                if (result.isAuthenticated) {
                  // Broadcast fresh tokens to all tabs after SSO
                  // This handles the case where this tab lost leadership during redirect
                  this.authSyncService.handlePostSSOTokenBroadcast();

                  // Wait for user to be in store before navigating
                  await this.waitForUserOrProceed();

                  const returnUrl = this.getAndClearReturnUrl();
                  this.router.navigateByUrl(returnUrl);
                } else {
                  // NOTE: Authentication failed, attempt to re-authorize
                  this.oidcSecurityService.authorize();
                }
              },
              error: () => {
                // NOTE: Don't wait for user in store - auth failed, so there's no user
                // Guards will redirect to login if needed
                // eslint-disable-next-line no-console
                console.log(
                  '[AuthCallback] checkAuth error, navigating to home'
                );
                const returnUrl = this.getAndClearReturnUrl();
                this.router.navigateByUrl(returnUrl);
              },
            });
          },
          error: () => {
            // NOTE: Don't wait for user in store - auth check failed, so there's no user
            // Guards will redirect to login if needed
            // eslint-disable-next-line no-console
            console.log(
              '[AuthCallback] isAuthenticated$ error, navigating to home'
            );
            const returnUrl = this.getAndClearReturnUrl();
            this.router.navigateByUrl(returnUrl);
          },
        });
    } else {
      // NOTE: No code or state found, redirecting to home as a fallback
      // Don't wait for user in store - no OAuth flow was initiated, so there's no user
      // Guards will redirect to login if needed
      // eslint-disable-next-line no-console
      console.log('[AuthCallback] No OAuth params, navigating to home');
      const returnUrl = this.getAndClearReturnUrl();
      this.router.navigateByUrl(returnUrl);
    }
  }
}
